import { emitEvent, PopupParams } from '@tma.js/sdk';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';

const PopupDialog = styled.dialog`
    box-sizing: border-box;
    width: min(320px, calc(100vw - 48px));
    padding: 24px 20px 12px;
    border: 0;
    border-radius: 16px;
    background: var(--tg-theme-bg-color, #17212b);
    color: var(--tg-theme-text-color, #f5f5f5);
    font-family: var(--tgui--font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
    box-shadow: 0 12px 48px #0006;

    &::backdrop {
        background: #0008;
    }

    h2 {
        margin: 0 0 12px;
        font-size: 20px;
        line-height: 26px;
    }

    p {
        margin: 0;
        font-size: 15px;
        line-height: 22px;
    }

    footer {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 4px;
        margin-top: 20px;
    }

    button {
        min-height: 44px;
        padding: 8px 12px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: var(--tgui--button_color, #5288c1);
        font: inherit;
        font-weight: 600;
        cursor: pointer;
    }

    button[data-destructive='true'] {
        color: var(--tg-theme-destructive-text-color, #ec3942);
    }
`;

function MockTelegramPopup({ title, message, buttons, onClose }: PopupParams & { onClose: (buttonId?: string) => void }) {
    const dialog = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        dialog.current?.showModal();
    }, []);

    return (
        <PopupDialog
            ref={dialog}
            aria-label={title || message}
            aria-describedby="mock-telegram-popup-message"
            onCancel={event => {
                event.preventDefault();
                onClose();
            }}
        >
            {title && <h2>{title}</h2>}
            <p id="mock-telegram-popup-message">{message}</p>
            <footer>
                {(buttons?.length ? buttons : [{ id: 'ok', type: 'ok' as const }]).map(button => (
                    <button
                        key={button.id}
                        data-popup-button-id={button.id}
                        data-destructive={button.type === 'destructive'}
                        onClick={() => onClose(button.id)}
                    >
                        {'text' in button ? button.text : button.type === 'cancel' ? 'Cancel' : button.type === 'close' ? 'Close' : 'OK'}
                    </button>
                ))}
            </footer>
        </PopupDialog>
    );
}

export function showMockTelegramPopup(params: PopupParams) {
    const container = document.createElement('div');
    document.body.append(container);
    const root = createRoot(container);
    const onClose = (buttonId?: string) => {
        root.unmount();
        container.remove();
        emitEvent('popup_closed', { button_id: buttonId });
    };
    root.render(<MockTelegramPopup {...params} onClose={onClose} />);
}
